import React, { useState, useEffect } from "react";
import axios from "../../../api/axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import styles from './AttendeeEditForm.module.css';

const AttendeeEditForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { attendeeid } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  
  // Get eventid from navigation state
  const eventidFromState = location.state?.eventid;
  
  const [formData, setFormData] = useState({
    BPAttendeeType: '',
    BPAttendeeFN: '',
    BPAttendeeLN: '',
    BPAttendeeEmail: '',
    BPAttendeeStatus: '',
    BPAttendeePhone: '',
    BPAttendeeGroup: '',
    BPAttendeeDepartureDate: '',
    BPAttendeeArrivalDate: '',
    BPAttendeeChurch: '',
    BPAttendeeAddress: '',
    BPAttendeeCity: '',
    BPAttendeeState: '',
    BPAttendeeZip: '',
    BPEventID: ''
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [registrationOptions, setRegistrationOptions] = useState([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(false);

  const statusOptions = [
    { value: '', label: 'Please select' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Denied', label: 'Denied' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  // Fetch attendee data
  useEffect(() => {
    const fetchAttendeeData = async () => {
      if (!attendeeid) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        let attendeeData = null;
        
        // If we have eventid from state, fetch all attendees for that event
        if (eventidFromState) {
          const response = await axios.get(`/api/attendees/${eventidFromState}`);
          const attendees = response.data;
          
          // Find the specific attendee by ID
          attendeeData = attendees.find(a => a.BPAttendeeID === parseInt(attendeeid));
          
          if (!attendeeData) {
            throw new Error('Attendee not found');
          }
        } else {
          // Fallback: fetch single attendee directly by ID
          const response = await axios.get(`/api/attendees/single/${attendeeid}`);
          attendeeData = response.data;
        }
        
        // Format dates for input fields (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        //console.log('Attendee Data:',attendeeData);

        // Backfill blank attendee fields from the user's profile (CMS_User +
        // CMS_UserSettings) so the edit form mirrors what's already on the user.
        // Attendee values always win when present.
        let userProfile = null;
        let userSettings = null;
        if (attendeeData.BPAttendeeUserID) {
          const [userResp, settingsResp] = await Promise.allSettled([
            axios.get(`/api/users/${attendeeData.BPAttendeeUserID}`),
            axios.get(`/api/users/usersettings/${attendeeData.BPAttendeeUserID}`)
          ]);
          if (userResp.status === 'fulfilled') userProfile = userResp.value?.data || null;
          if (settingsResp.status === 'fulfilled') userSettings = settingsResp.value?.data || null;
        }

        const pick = (...vals) => {
          for (const v of vals) {
            if (v !== null && v !== undefined && String(v).trim() !== '') return v;
          }
          return '';
        };

        setFormData({
          BPAttendeeType: attendeeData.BPAttendeeType || '',
          BPAttendeeFN: pick(attendeeData.BPAttendeeFN, userProfile?.FirstName),
          BPAttendeeLN: pick(attendeeData.BPAttendeeLN, userProfile?.LastName),
          BPAttendeeEmail: pick(attendeeData.BPAttendeeEmail, userProfile?.Email),
          BPAttendeeStatus: attendeeData.BPAttendeeStatus || 'Pending',
          BPAttendeePhone: pick(attendeeData.BPAttendeePhone, userSettings?.BP_G_Mob_Phone),
          BPAttendeeGroup: attendeeData.BPAttendeeGroup || '',
          BPAttendeeDepartureDate: formatDateForInput(attendeeData.BPAttendeeDepartureDate),
          BPAttendeeArrivalDate: formatDateForInput(attendeeData.BPAttendeeArrivalDate),
          BPAttendeeChurch: pick(attendeeData.BPAttendeeChurch, userSettings?.BP_C_ChurchName),
          BPAttendeeAddress: pick(attendeeData.BPAttendeeAddress, userSettings?.BP_G_Address),
          BPAttendeeCity: pick(attendeeData.BPAttendeeCity, userSettings?.BP_G_AddressCity),
          BPAttendeeState: pick(attendeeData.BPAttendeeState, userSettings?.BP_G_AddressState),
          BPAttendeeZip: pick(attendeeData.BPAttendeeZip, userSettings?.BP_G_AddressZip),
          BPEventID: attendeeData.BPEventID || eventidFromState || ''
        });

        enqueueSnackbar('Attendee data loaded successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error fetching attendee data:', error);
        setError('Failed to load attendee data. Please try again.');
        enqueueSnackbar('Failed to load attendee data', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendeeData();
  }, [attendeeid, eventidFromState, enqueueSnackbar]);

  // Fetch registrations for this event to power Registration Type dropdown
  useEffect(() => {
    const eventId = formData.BPEventID;
    if (!eventId) return;

    let cancelled = false;
    const run = async () => {
      setIsLoadingRegistrations(true);
      try {
        const resp = await axios.get(`/api/registrations/events/${eventId}`);
        const regs = Array.isArray(resp.data) ? resp.data : [];
        const options = regs
  .filter((r) => r?.RegistrationName)
  .map((r) => ({
    name: String(r.RegistrationName).trim(),
    cost: parseFloat(r.RegistrationCost ?? 0) || 0,
  }));

        if (!cancelled) {
          setRegistrationOptions(options);
          // Force blank if current value doesn't match any registration
          if (formData.BPAttendeeType && !options.some(o => o.name === formData.BPAttendeeType)) {
            setFormData((prev) => ({ ...prev, BPAttendeeType: '' }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setRegistrationOptions([]);
          enqueueSnackbar('Failed to load registrations for this event', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setIsLoadingRegistrations(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally depend on event id only; the mismatch check runs after options load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.BPEventID, enqueueSnackbar]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.BPAttendeeFN || formData.BPAttendeeFN.trim() === '') {
      setError('First Name is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!formData.BPAttendeeLN || formData.BPAttendeeLN.trim() === '') {
      setError('Last Name is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (!formData.BPAttendeeEmail || formData.BPAttendeeEmail.trim() === '') {
      setError('Email Address is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.BPAttendeeType || formData.BPAttendeeType.trim() === '') {
      setError('Registration Type is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Convert empty date strings to null for Prisma
      const sanitizedData = {
        ...formData,
        BPAttendeeDepartureDate: formData.BPAttendeeDepartureDate || null,
        BPAttendeeArrivalDate: formData.BPAttendeeArrivalDate || null,
      };
      
      const attendeeResult = await axios.put(`/api/attendees/${attendeeid}`, sanitizedData);

      try{
      //add order with attendee
        await createOrder(attendeeResult);
      }catch(e){
        console.error(e);
      }
      
      // Show success message
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
      
      enqueueSnackbar('Attendee updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating attendee:', error);
      setError('Failed to update attendee. Please try again.');
      enqueueSnackbar('Failed to update attendee', { variant: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const createOrder = async (attendeeResult) => {
  const attendee = attendeeResult?.data;
  if (!attendee?.BPAttendeeID) {
    enqueueSnackbar('Attendee saved but order could not be created — no attendee ID returned', { variant: 'warning' });
    return;
  }

  const reg = registrationOptions.find((o) => o.name === formData.BPAttendeeType);
  const regCost = reg?.cost ?? 0;

  try {
    const orderPayload = {
      OrderDate:  new Date().toISOString(),
      Products:   `${formData.BPAttendeeType} - ${attendee.BPAttendeeID}`,
      PaidToDate: 0,
      Balance:    regCost,
      UserID:     attendee.BPAttendeeUserID ?? null,
      Notes:      `Admin-created registration for event ${formData.BPEventID}`,
      createdAt:  new Date().toISOString(),
      updatedAt:  new Date().toISOString(),
    };

    const orderRes = await axios.post('/api/orders', orderPayload);
    const order = orderRes.data;

    await axios.post('/api/orders/order-items', {
      OrderID:     order.OrderID,
      Quantity:    1,
      UnitPrice:   regCost,
      TotalPrice:  regCost,
      EventID:     formData.BPEventID,
      AttendeeID:  attendee.BPAttendeeID,
      Description: `Event ${formData.BPEventID} — ${formData.BPAttendeeType}`,
    });

    // Link the order back to the attendee record
    await axios.put(`/api/attendees/${attendee.BPAttendeeID}`, {
      BPAttendeeBalance: regCost,
      BPAttendeeOrderID: order.OrderID,
    });

    enqueueSnackbar('Order created successfully', { variant: 'success' });
  } catch (e) {
    console.error('Failed to create order:', e);
    enqueueSnackbar('Attendee saved but order creation failed: ' + (e?.response?.data?.error || e.message), { variant: 'warning' });
  }
};

  const handleCancel = () => {
    if (formData.BPEventID) {
      navigate(`/admin/events/attendees/${formData.BPEventID}`);
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.headerSection}>
        <Button
          type="button"
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          className={styles.backButton}
        >
          Back to Attendees
        </Button>
        <Typography variant="h4" className={styles.pageTitle}>
          Edit Attendee
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className={styles.errorAlert} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className={styles.successAlert}>
          Attendee updated successfully!
        </Alert>
      )}

      <Paper className={styles.formPaper}>
        <Typography variant="h6" className={styles.formTitle}>
          ATTENDEE INFORMATION
        </Typography>

        <Grid container spacing={3}>
          {/* Registration Type */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <FormControl fullWidth variant="outlined">
                <InputLabel>Registration Type</InputLabel>
                <Select
                  name="BPAttendeeType"
                  value={formData.BPAttendeeType || ''}
                  onChange={handleFieldChange}
                  label="Registration Type"
                  displayEmpty
                  disabled={isLoadingRegistrations || registrationOptions.length === 0}
                  className={styles.selectField}
                >
                  <MenuItem
                    value=""
                    className={
                      formData.BPAttendeeType ? styles.selectMenuItemHidden : styles.selectMenuItem
                    }
                  >
                    {isLoadingRegistrations
                      ? 'Loading registrations...'
                      : registrationOptions.length === 0
                        ? 'No registrations found'
                        : 'Select registration type'}
                  </MenuItem>
                  {registrationOptions.map((reg) => (
  <MenuItem key={reg.name} value={reg.name} className={styles.selectMenuItem}>
    {reg.name}
  </MenuItem>
))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  name="BPAttendeeStatus"
                  value={formData.BPAttendeeStatus || ''}
                  onChange={handleFieldChange}
                  label="Status"
                  displayEmpty
                  className={styles.selectField}
                >
                  {statusOptions.map((option) => (
                    <MenuItem 
                      key={option.value || 'empty'} 
                      value={option.value}
                      className={option.value === '' && formData.BPAttendeeStatus ? styles.selectMenuItemHidden : styles.selectMenuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* First Name */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                First Name *
              </Typography>
              <TextField
                fullWidth
                required
                label="First Name"
                name="BPAttendeeFN"
                value={formData.BPAttendeeFN}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Last Name *
              </Typography>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="BPAttendeeLN"
                value={formData.BPAttendeeLN}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Email Address */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Email Address *
              </Typography>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                name="BPAttendeeEmail"
                value={formData.BPAttendeeEmail}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Phone
              </Typography>
              <TextField
                fullWidth
                label="Phone"
                name="BPAttendeePhone"
                value={formData.BPAttendeePhone}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Team Name */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Team Name
              </Typography>
              <TextField
                fullWidth
                label="Team Name"
                name="BPAttendeeGroup"
                value={formData.BPAttendeeGroup}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Church */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Church
              </Typography>
              <TextField
                fullWidth
                label="Church"
                name="BPAttendeeChurch"
                value={formData.BPAttendeeChurch}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Arrival Date */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Arrival Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                label="Arrival Date"
                name="BPAttendeeArrivalDate"
                value={formData.BPAttendeeArrivalDate}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Departure Date */}
          <Grid item xs={12} sm={6}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Departure Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                label="Departure Date"
                name="BPAttendeeDepartureDate"
                value={formData.BPAttendeeDepartureDate}
                onChange={handleFieldChange}
                InputLabelProps={{ shrink: true }}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Address
              </Typography>
              <TextField
                fullWidth
                label="Address"
                name="BPAttendeeAddress"
                value={formData.BPAttendeeAddress}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={4}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                City
              </Typography>
              <TextField
                fullWidth
                label="City"
                name="BPAttendeeCity"
                value={formData.BPAttendeeCity}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={4}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                State
              </Typography>
              <TextField
                fullWidth
                label="State"
                name="BPAttendeeState"
                value={formData.BPAttendeeState}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

          {/* Zip */}
          <Grid item xs={12} sm={4}>
            <Box className={styles.fieldContainer}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                Zip Code
              </Typography>
              <TextField
                fullWidth
                label="Zip Code"
                name="BPAttendeeZip"
                value={formData.BPAttendeeZip}
                onChange={handleFieldChange}
                className={styles.textField}
              />
            </Box>
          </Grid>

        </Grid>

        <Box className={styles.formActions}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isSaving}
          >
            {isSaving ? 'Updating...' : 'Update Attendee'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AttendeeEditForm;
